import { useRef, useState, type DragEvent } from "react";
import styled, { keyframes, css } from "styled-components";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import type {
  ChatSession,
  ChatSessionMessage,
} from "@KassaroAB/shared/types/chatSession";
import type { ChangeRequestView } from "@KassaroAB/shared/types/changeRequest";
import type { AgentProgressStep } from "../api/storeChatSessionsStreamApi";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Button } from "./Button";
import { ChatBubble } from "./ChatBubble";
import { ChatComposer, type ChatComposerHandle } from "./ChatComposer";
import { ChangeRequestBubble } from "./ChangeRequestBubble";
import { ChatChips } from "./ChatChips";
import { splitChatQuickReplies } from "@KassaroAB/shared/utils/chatQuickReplies";
import { ChatMessageRole } from "@KassaroAB/shared/types/enums/ChatMessageRole";
import { StoreTemplateId } from "@KassaroAB/shared/types/enums/StoreTemplateId";
import { ChangeRequestStatus } from "@KassaroAB/shared/types/enums/ChangeRequestStatus";
import {
  getActiveProgressTool,
  getAgentProgressLabelKey,
} from "../utils/agentProgressLabel";

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const DetailPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

const DetailHeader = styled.div`
  padding: ${spacing.x2}px ${spacing.x3}px;
  border-bottom: 1px solid ${colors.border};
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const DetailContent = styled.div<{ $isDragging?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;

  ${({ $isDragging }) =>
    $isDragging
      ? css`
          box-shadow: inset 0 0 0 2px ${colors.primary};
          outline: 2px dashed ${colors.primary};
          outline-offset: -4px;
        `
      : ""}
`;

const DropOverlay = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? "flex" : "none")};
  position: absolute;
  inset: 0;
  z-index: 20;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  background-color: hsla(220, 85%, 7%, 0.06);
`;

const DropOverlayText = styled.p`
  margin: 0;
  padding: ${spacing.x2}px ${spacing.x3}px;
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.primary};
  text-align: center;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing.x3}px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  padding: ${spacing.x2}px ${spacing.x3}px;
`;

const ProgressPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  padding: ${spacing.x2}px ${spacing.x3}px;
  border-top: 1px solid ${colors.border};
`;

const ProgressCurrent = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const ProgressHistory = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const ProgressHistoryItem = styled.li`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  opacity: 0.65;
`;

const SpinWrap = styled.span`
  display: inline-flex;
  animation: ${spin} 0.8s linear infinite;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.x4}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  text-align: center;
`;

const ErrorBanner = styled.div`
  padding: ${spacing.x2}px ${spacing.x3}px;
  border-radius: ${borderRadius.regular}px;
  background-color: rgba(235, 87, 87, 0.12);
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-bottom: ${spacing.x2}px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const StarterPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${spacing.x3}px;
  padding: ${spacing.x4}px;
  max-width: 640px;
  margin: 0 auto;
`;

const StarterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const StarterTitle = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
`;

const StarterHint = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const STORE_SETUP_STARTERS = Object.values(StoreTemplateId);

const TASK_STARTERS = ["addProducts", "menuPhoto", "layout", "receipt"];

type Props = {
  session?: ChatSession;
  messages: ChatSessionMessage[] | undefined;
  isLoadingMessages: boolean;
  messagesError: Error | null;
  onRefetchMessages: () => void;
  onSendMessage: (text: string, files: File[]) => void;
  isSendingMessage: boolean;
  sendError: Error | null;
  progressSteps: AgentProgressStep[];
  changeRequest: ChangeRequestView | null | undefined;
  changeRequestHistory: ChangeRequestView[] | undefined;
  isLoadingChangeRequest: boolean;
  onApproveChangeRequest: () => void;
  isApprovingChangeRequest: boolean;
  approveError: Error | null;
};

export function ChatDetailPane({
  session,
  messages,
  isLoadingMessages,
  messagesError,
  onRefetchMessages,
  onSendMessage,
  isSendingMessage,
  sendError,
  progressSteps,
  changeRequest,
  changeRequestHistory,
  isLoadingChangeRequest,
  onApproveChangeRequest,
  isApprovingChangeRequest,
  approveError,
}: Props) {
  const { t } = useTranslation();
  const composerRef = useRef<ChatComposerHandle>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);

  const canAcceptDrop =
    !isLoadingMessages && !messagesError && !isSendingMessage;

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (!canAcceptDrop) return;
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer.types.includes("Files")) return;
    dragDepth.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!canAcceptDrop) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!canAcceptDrop) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!canAcceptDrop) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setIsDragging(false);
    const incoming = Array.from(e.dataTransfer.files ?? []);
    if (incoming.length > 0) {
      composerRef.current?.addFiles(incoming);
    }
  };

  const toChips = (group: "setup" | "tasks", keys: string[]) =>
    keys.map((key) => ({
      key,
      label: t(`storeChatSessions.starters.${group}.${key}.label`),
      message: t(`storeChatSessions.starters.${group}.${key}.message`),
    }));

  const lastMessage = messages?.[messages.length - 1];
  const quickReplies =
    lastMessage?.role === ChatMessageRole.ASSISTANT && !isSendingMessage
      ? splitChatQuickReplies(lastMessage.content).quickReplies
      : [];

  const approvedChangeRequests = (changeRequestHistory ?? []).filter(
    (entry) => entry.status === ChangeRequestStatus.APPROVED,
  );
  const timeline = [
    ...(messages ?? []).map((message, index) => ({
      key: message.id ?? `${index}-${message.role}`,
      at: message.createdAt,
      message,
      changeRequest: undefined,
    })),
    ...approvedChangeRequests.map((entry) => ({
      key: `changeRequest-${entry.id}`,
      at: entry.approvedAt ?? entry.updatedAt,
      message: undefined,
      changeRequest: entry,
    })),
  ].sort((a, b) => a.at.localeCompare(b.at));

  const activeProgressTool = getActiveProgressTool(progressSteps);
  const completedProgressSteps = progressSteps.filter(
    (step) => step.phase === "done",
  );

  const getProgressLabel = (tool: string) => {
    const labelKey = getAgentProgressLabelKey(tool);
    if (t(labelKey) !== labelKey) {
      return t(labelKey);
    }
    return t("storeChatSessions.agentProgress.fallback");
  };

  return (
    <DetailPane>
      <DetailHeader>
        {session
          ? `${t("storeChatSessions.sessionIdPrefix")} ${session.id}`
          : t("storeChatSessions.newChat")}
      </DetailHeader>
      {messagesError && (
        <ErrorBanner>
          {messagesError instanceof Error
            ? messagesError.message
            : t("storeChatSessions.messagesLoadFailed")}
          <Button
            type="button"
            $color="secondary"
            onClick={() => onRefetchMessages()}
          >
            {t("common.retry")}
          </Button>
        </ErrorBanner>
      )}
      <DetailContent
        $isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <DropOverlay $visible={isDragging}>
          <DropOverlayText>
            {t("storeChatSessions.dropFilesHint")}
          </DropOverlayText>
        </DropOverlay>
        {isLoadingMessages && (
          <StatusLine>
            <SpinWrap>
              <Loader2 size={16} aria-hidden />
            </SpinWrap>
            {t("common.loading")}
          </StatusLine>
        )}
        {!isLoadingMessages && !messagesError && (
          <>
            <MessageList>
              {timeline.length > 0 ? (
                timeline.map((item) =>
                  item.message ? (
                    <ChatBubble
                      key={item.key}
                      role={item.message.role}
                      content={splitChatQuickReplies(item.message.content).text}
                      attachments={item.message.attachments}
                    />
                  ) : item.changeRequest ? (
                    <MessageRow key={item.key}>
                      <ChangeRequestBubble changeRequest={item.changeRequest} />
                    </MessageRow>
                  ) : null,
                )
              ) : session ? (
                <EmptyState>{t("storeChatSessions.noMessages")}</EmptyState>
              ) : (
                <StarterPanel>
                  <StarterSection>
                    <StarterTitle>
                      {t("storeChatSessions.starters.setupTitle")}
                    </StarterTitle>
                    <StarterHint>
                      {t("storeChatSessions.starters.setupHint")}
                    </StarterHint>
                    <ChatChips
                      chips={toChips("setup", STORE_SETUP_STARTERS)}
                      onSelect={(message) => onSendMessage(message, [])}
                      disabled={isSendingMessage}
                    />
                  </StarterSection>
                  <StarterSection>
                    <StarterTitle>
                      {t("storeChatSessions.starters.tasksTitle")}
                    </StarterTitle>
                    <ChatChips
                      chips={toChips("tasks", TASK_STARTERS)}
                      onSelect={(message) => onSendMessage(message, [])}
                      disabled={isSendingMessage}
                    />
                  </StarterSection>
                </StarterPanel>
              )}
              {quickReplies.length > 0 && (
                <ChatChips
                  chips={quickReplies.map((reply) => ({
                    key: reply,
                    label: reply,
                    message: reply,
                  }))}
                  onSelect={(message) => onSendMessage(message, [])}
                />
              )}
              {!isLoadingChangeRequest &&
                changeRequest != null &&
                changeRequest.status !== ChangeRequestStatus.APPROVED && (
                  <MessageRow>
                    <ChangeRequestBubble
                      changeRequest={changeRequest}
                      onApprove={onApproveChangeRequest}
                      isApproving={isApprovingChangeRequest}
                      approveError={approveError}
                    />
                  </MessageRow>
                )}
            </MessageList>
            {isSendingMessage && (
              <ProgressPanel>
                <ProgressCurrent>
                  <SpinWrap>
                    <Loader2 size={16} aria-hidden />
                  </SpinWrap>
                  {getProgressLabel(activeProgressTool ?? "thinking")}
                </ProgressCurrent>
                {completedProgressSteps.length > 0 && (
                  <ProgressHistory>
                    {completedProgressSteps.map((step, index) => (
                      <ProgressHistoryItem key={`${step.tool}-${index}`}>
                        {getProgressLabel(step.tool)}
                      </ProgressHistoryItem>
                    ))}
                  </ProgressHistory>
                )}
              </ProgressPanel>
            )}
            <ChatComposer
              ref={composerRef}
              onSend={onSendMessage}
              isSending={isSendingMessage}
              sendError={sendError}
            />
          </>
        )}
      </DetailContent>
    </DetailPane>
  );
}
